import { useQueries } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Agent, AgentConnection, AgentConnectionRequest } from "@/types";

// #design-pass W10 (Graph): A2A-Connections liegen pro Agent (Sub-Resource). Für den
// Graphen fächern wir über alle Agents auf (useQueries, wie useAgentsCostSummary) und
// deduplizieren über die id — jede Connection/Request taucht bei beiden Endpunkten auf.
// QueryKeys identisch zu queries/connections.ts → geteilter Cache, kein Doppel-Fetch.
export function useAgentGraph(agents: Agent[] | undefined, enabled: boolean) {
  const { session, logout } = useAuth();
  const ids = (agents ?? []).filter((a) => !a.revokedAt).map((a) => a.id);

  const connQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: queryKeys.subList("agents", id, "connections"),
      queryFn: async () => {
        const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
        const res = await client.get<{ connections: AgentConnection[] }>(`/agents/${id}/connections`);
        return res.connections;
      },
      enabled: enabled && !!session,
    })),
  });

  const reqQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: [...queryKeys.subList("agents", id, "connection-requests"), "pending"],
      queryFn: async () => {
        const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
        const res = await client.get<{ requests: AgentConnectionRequest[] }>(
          `/agents/${id}/connection-requests?status=pending`,
        );
        return res.requests;
      },
      enabled: enabled && !!session,
    })),
  });

  const connMap = new Map<string, AgentConnection>();
  for (const q of connQueries) for (const c of q.data ?? []) if (c.status === "active") connMap.set(c.id, c);

  const reqMap = new Map<string, AgentConnectionRequest>();
  for (const q of reqQueries) for (const r of q.data ?? []) if (r.status === "pending") reqMap.set(r.id, r);

  return {
    connections: [...connMap.values()],
    requests: [...reqMap.values()],
    isLoading: enabled && (connQueries.some((q) => q.isLoading) || reqQueries.some((q) => q.isLoading)),
    isError: connQueries.some((q) => q.isError) || reqQueries.some((q) => q.isError),
  };
}
